import React, { useMemo } from "react";
import classNames from "classnames";
import { Context, Hooks, Util } from "@xt/xtbase";
import ReturnRateChart from "@/components/Pages/aicopy/components/ReturnRateChart";
import strategyIconFallback from "assets/images/aicopy/eb3a5f42-0255-4b85-82c6-bd5462a3928b.png";
import {
  IconArrowRight,
  IconBot,
  IconCompass,
  IconLayoutDashboard,
  IconSparkles,
  IconTrophy,
} from "./icons";
import styles from "./index.module.less";

const { XtContext } = Context;
const { useTranslation } = Hooks;
const { getUrl } = Util;

function formatReturnRate(raw) {
  const s = String(raw ?? "")
    .replace(/%/g, "")
    .trim();
  const n = parseFloat(s);
  if (Number.isNaN(n)) return "0%";
  if (n > 0) return `+${s}%`;
  if (n < 0) return `${s}%`;
  return "0%";
}

function isReturnUp(raw) {
  const n = parseFloat(String(raw ?? "").replace(/%/g, "").trim());
  return !Number.isNaN(n) && n >= 0;
}

export default function AiLabSection({ strategies = [], strategyReturnTrends = {} }) {
  const t = useTranslation();
  const [appState] = React.useContext(XtContext);
  const isLogin = appState?.loginStatus === 1;
  const top = useMemo(() => (Array.isArray(strategies) ? strategies.slice(0, 4) : []), [strategies]);

  const entries = [
    {
      icon: IconCompass,
      title: t("home.pcAiArena") || "Arena",
      desc: t("home.pcAiArenaDesc") || "Watch frontier AI models trade live, head to head.",
      href: getUrl("/ai-lab?tab=arena"),
    },
    {
      icon: IconBot,
      title: t("home.pcAiAgents") || "Agents",
      desc: t("home.pcAiAgentsDesc") || "Discover autonomous agents and deploy them in one tap.",
      href: getUrl("/aicopy"),
    },
    {
      icon: IconTrophy,
      title: t("home.pcAiLeaderboard") || "Leaderboard",
      desc: t("home.pcAiLeaderboardDesc") || "Top agents ranked by verified return.",
      href: getUrl("/ai-lab?tab=leaderboard"),
    },
    {
      icon: IconLayoutDashboard,
      title: t("home.pcAiWorkspace") || "Workspace",
      desc: t("home.pcAiWorkspaceDesc") || "Your agents, positions and live execution.",
      href: isLogin ? getUrl("/ai-lab?tab=workspace") : getUrl("/accounts/login"),
      gated: !isLogin,
    },
  ];

  return (
    <section className={styles.section}>
      <div className={styles.sectionHead}>
        <div>
          <span className={styles.pill}>
            <IconSparkles size={14} className={styles.brandIcon} /> {t("home.pcAiLabBadge") || "AI Lab"}
          </span>
          <h2 className={styles.sectionTitle}>
            {t("home.pcAiLabTitle") || "Deploy AI agents that trade for you"}
          </h2>
        </div>
        <a href={getUrl("/ai-lab?tab=arena")} className={styles.sectionLink}>
          {t("home.pcExploreAiLab") || "Explore AI Lab"} <IconArrowRight size={16} />
        </a>
      </div>

      <div className={styles.aiLabGrid}>
        <div className={styles.aiEntryGrid}>
          {entries.map((e) => {
            const Icon = e.icon;
            return (
              <a key={e.title} href={e.href} className={classNames(styles.card, styles.cardHover, styles.aiEntry)}>
                <span className={styles.frostTile}>
                  <Icon size={20} className={styles.frostIcon} />
                </span>
                <h3 className={styles.cardTitle}>
                  {e.title}
                  {e.gated ? (
                    <span className={styles.gatedTag}>{t("home.logIn") || "Sign in"}</span>
                  ) : null}
                </h3>
                <p className={styles.cardDesc}>{e.desc}</p>
              </a>
            );
          })}
        </div>

        <div className={classNames(styles.card, styles.topAgentsCard)}>
          <div className={styles.topAgentsHead}>
            <span className={styles.topAgentsTitle}>{t("home.pcTopAgents") || "Top Agents"}</span>
            <a href={getUrl("/aicopy")} className={styles.topAgentsLink}>
              {t("home.pcViewAll") || "View all"}
            </a>
          </div>
          <ul className={styles.topAgentsList}>
            {top.map((a, i) => {
              const trend = strategyReturnTrends[a.strategyId];
              const hasTrend = Array.isArray(trend) && trend.length > 0;
              const roiRaw = a.return90d ?? a.return_90d;
              return (
                <li key={a.strategyId || i}>
                  <a href={getUrl(`/aicopy/agents/${encodeURIComponent(a.strategyId)}`)} className={styles.topAgentRow}>
                    <span className={styles.topAgentRank}>{i + 1}</span>
                    <img
                      src={a.avatarUrl || strategyIconFallback}
                      alt=""
                      className={styles.topAgentAvatar}
                    />
                    <div className={styles.topAgentMeta}>
                      <div className={styles.topAgentName}>{a.strategyName}</div>
                      <div className={styles.topAgentSub}>
                        {a.followerCount ?? 0} {t("aicopy.followers") || "followers"}
                      </div>
                    </div>
                    {hasTrend ? (
                      <div className={styles.topAgentSpark}>
                        <ReturnRateChart data={trend} />
                      </div>
                    ) : null}
                    <span
                      className={classNames(
                        styles.topAgentRoi,
                        isReturnUp(roiRaw) ? styles.up : styles.down
                      )}
                    >
                      {formatReturnRate(roiRaw)}
                    </span>
                  </a>
                </li>
              );
            })}
            {!top.length ? (
              <li className={styles.moversEmpty}>{t("home.pcLoadingMarkets") || "Loading…"}</li>
            ) : null}
          </ul>
          <a href={getUrl("/aicopy")} className={styles.browseAgentsBtn}>
            <IconBot size={16} /> {t("home.pcBrowseAgents") || "Browse Agents"}
          </a>
        </div>
      </div>
    </section>
  );
}
